import { createI18n } from 'vue-i18n'
import { apiClient } from '@/api/client'

/**
 * i18n DB-backed: i messaggi NON sono file statici ma vengono caricati a runtime dall'API
 * (`GET /api/v2/i18n/messages?lang=`), che restituisce un bundle già risolto (merge
 * globale⊕azienda + fallback IT lato server). Cache in localStorage per primo paint istantaneo.
 *
 * Lingua di default: IT. La scelta dell'utente è salvata solo nel browser (localStorage).
 */
export const SUPPORTED_LOCALES = ['it', 'en', 'fr', 'de'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: AppLocale = 'it'

/** Chiave localStorage con la lingua scelta (letta anche dall'apiClient per Accept-Language). */
export const LOCALE_STORAGE_KEY = 'i18n:locale'

function bundleKey(lang: string): string {
  return `i18n:bundle:${lang}`
}

function isSupported(l: string | null): l is AppLocale {
  return l != null && (SUPPORTED_LOCALES as readonly string[]).includes(l)
}

/** Lingua corrente da localStorage, con fallback al default. */
export function storedLocale(): AppLocale {
  const l = localStorage.getItem(LOCALE_STORAGE_KEY)
  return isSupported(l) ? l : DEFAULT_LOCALE
}

export function persistLocale(lang: AppLocale): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, lang)
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: storedLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  missingWarn: false,
  fallbackWarn: false,
  // I messaggi vengono iniettati a runtime via setLocaleMessage (hydrate + fetch).
  messages: {},
})

/**
 * Idrata in modo SINCRONO i messaggi dalla cache localStorage (primo paint senza flash).
 * Va chiamata prima del mount per la lingua iniziale e al cambio lingua.
 */
export function hydrateFromCache(lang: string): void {
  try {
    const raw = localStorage.getItem(bundleKey(lang))
    if (raw) {
      const cached = JSON.parse(raw) as { version: string; messages: Record<string, string> }
      if (cached?.messages) {
        i18n.global.setLocaleMessage(lang, cached.messages)
      }
    }
  } catch {
    // cache corrotta: ignora, verrà ricaricata dall'API
  }
}

/**
 * Carica il bundle dall'API e lo applica + lo mette in cache. Il bundle è già risolto col
 * fallback IT lato server, quindi contiene tutte le chiavi anche per le lingue non IT.
 */
export async function loadLocaleMessages(lang: string): Promise<void> {
  try {
    const { data } = await apiClient.get<{ lang: string; version: string; messages: Record<string, string> }>(
      '/api/v2/i18n/messages',
      { params: { lang } },
    )
    i18n.global.setLocaleMessage(data.lang, data.messages)
    localStorage.setItem(bundleKey(data.lang), JSON.stringify({ version: data.version, messages: data.messages }))
  } catch {
    // offline / errore: si resta sul bundle in cache (o sulle chiavi come fallback)
  }
}

/** Traduzione fuori dai componenti: `fallback` = testo italiano di oggi, usato se manca la chiave. */
export function tr(key: string, fallback: string, params?: Record<string, unknown>): string {
  const g = i18n.global
  if (!g.te(key) && !g.te(key, DEFAULT_LOCALE)) {
    return params ? fallback.replace(/\{(\w+)\}/g, (_m, p: string) => String(params[p] ?? `{${p}}`)) : fallback
  }
  return params ? g.t(key, params) : g.t(key)
}

const LOCALE_TAGS: Record<AppLocale, string> = { it: 'it-IT', en: 'en-GB', fr: 'fr-FR', de: 'de-DE' }

/** Tag BCP47 della lingua corrente, SOLO per la visualizzazione di date/numeri (non per le date civili gg/MM/aaaa). */
export function localeTag(): string {
  return LOCALE_TAGS[i18n.global.locale.value as AppLocale] ?? 'it-IT'
}
